/**
 * Minimal typed event bus. This is what keeps the dependency direction in
 * CODE_RULES.md §2 honest: `viewer/` and `pdf/` emit, `ui/` subscribes, and
 * the feature layers never import the UI.
 */

export type Unsubscribe = () => void;

type Handler<T> = (payload: T) => void;

export class Emitter<Events extends Record<string, unknown>> {
  readonly #handlers = new Map<keyof Events, Set<Handler<never>>>();

  on<K extends keyof Events>(event: K, handler: Handler<Events[K]>): Unsubscribe {
    let set = this.#handlers.get(event);
    if (set === undefined) {
      set = new Set();
      this.#handlers.set(event, set);
    }
    // Every Handler<T> is assignable to Handler<never> (never is the bottom
    // type), so storage needs no assertion; only dispatch widens back.
    set.add(handler);
    return () => {
      this.#handlers.get(event)?.delete(handler);
    };
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.#handlers.get(event);
    if (set === undefined) return;
    // Copy before iterating: a handler may unsubscribe itself mid-dispatch.
    for (const handler of [...set]) {
      (handler as Handler<Events[K]>)(payload);
    }
  }

  dispose(): void {
    this.#handlers.clear();
  }
}
